import React, { useState, useEffect, useRef } from "react";
import { getGroupReviewerContent, submitChallengeAttempt } from "../../../services/api";

const cleanPrompt = (html) => {
  if (!html || typeof html !== "string") return "";
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

const extractChoiceLetter = (value) => {
  const text = String(value ?? "").trim();
  if (/^[A-H]$/i.test(text)) return text.toUpperCase();
  const match = text.match(/^([A-H])(?:\s*[:.)-]\s*|\s+)/i);
  return match ? match[1].toUpperCase() : null;
};

const stripChoiceLetter = (value) => {
  const text = String(value ?? "").trim();
  if (/^[A-H]$/i.test(text)) return "";
  return text.replace(/^([A-H])(?:\s*[:.)-]\s*|\s+)/i, "").trim();
};

const normalizeForCompare = (value) => {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/<[^>]*>/g, "")
    .replace(/[.,/#!$%^&*;:{}=\-_`~()?"']/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const isAnswerCorrect = (q, userAnswerRaw) => {
  if (!q || userAnswerRaw == null) return false;
  const userAnswer = String(userAnswerRaw).trim();
  if (!userAnswer || userAnswer === "__TIMEOUT__" || userAnswer === "__UNANSWERED__") {
    return false;
  }

  const options = Array.isArray(q.options) ? q.options : [];

  // Multiple Choice / True-False check
  if (options.length > 0) {
    const ansLetter = extractChoiceLetter(q.answer);
    if (ansLetter) {
      const userLetter = extractChoiceLetter(userAnswer);
      if (userLetter) return userLetter === ansLetter;

      const selectedIndex = options.findIndex(
        (opt) =>
          normalizeForCompare(stripChoiceLetter(opt)) ===
          normalizeForCompare(stripChoiceLetter(userAnswer))
      );
      if (selectedIndex >= 0 && selectedIndex < 8) {
        return String.fromCharCode(65 + selectedIndex) === ansLetter;
      }
    }

    const selectedLetter = extractChoiceLetter(userAnswer);
    const selectedIndex = selectedLetter ? selectedLetter.charCodeAt(0) - 65 : -1;
    const selectedText =
      selectedIndex >= 0 && selectedIndex < options.length ? options[selectedIndex] : userAnswer;

    return (
      normalizeForCompare(stripChoiceLetter(selectedText)) ===
      normalizeForCompare(stripChoiceLetter(q.answer))
    );
  }

  // Identification & Fill-in-the-blank check (direct text input)
  const u = normalizeForCompare(stripChoiceLetter(userAnswer));
  const c = normalizeForCompare(stripChoiceLetter(q.answer));
  if (!u || !c) return false;
  if (u === c) return true;

  // Tolerant comparison stripping common articles ("a", "an", "the")
  const stripArticles = (s) => s.replace(/^(a|an|the)\s+/i, "").trim();
  if (stripArticles(u) === stripArticles(c)) return true;

  return false;
};

const inferQuizType = (q) => {
  const options = Array.isArray(q?.options) ? q.options : [];
  const prompt = String(q?.prompt || q?.question || "");

  if (options.length >= 2) {
    const normalized = options.map((o) => String(o || "").trim().toLowerCase());
    if (normalized.includes("true") && normalized.includes("false")) return "true-false";
    return "mcq";
  }
  if (prompt.includes("_______") || prompt.includes("____") || prompt.includes("___")) {
    return "fill-blanks";
  }
  return "identification";
};

const ChallengeQuizModal = ({
  isOpen,
  onClose,
  challenge,
  groupId,
  onAttemptSubmitted,
}) => {
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [userAnswers, setUserAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(20);
  const [submitting, setSubmitting] = useState(false);
  const [streak, setStreak] = useState(0);

  const textInputRef = useRef(null);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);

  const timePerQ = challenge?.timePerQuestion || 20;

  // Load questions
  useEffect(() => {
    if (!isOpen || !challenge) return;

    let isMounted = true;
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        setErrorMsg("");
        setCurrentIndex(0);
        setSelectedOption(null);
        setTextInput("");
        setUserAnswers({});
        setStreak(0);

        const revId = challenge.reviewerId?._id || challenge.reviewerId || challenge.reviewer;
        const res = await getGroupReviewerContent(groupId, revId);
        const data = res?.data || {};

        const rawList =
          data.quiz ||
          data.questions ||
          data.quizQuestions ||
          (Array.isArray(data) ? data : []);

        const normalized = rawList.map((q, idx) => {
          let opts = [];
          if (Array.isArray(q.options)) {
            opts = q.options.map((o) => (typeof o === "string" ? o : o.text || o.option || String(o)));
          }
          const prompt = q.question || q.prompt || q.q || `Question ${idx + 1}`;
          const answer = q.answer || q.correctAnswer || q.correct || "";

          return {
            id: idx,
            prompt: cleanPrompt(prompt),
            options: opts,
            answer: typeof answer === "string" ? answer.trim() : String(answer),
          };
        });

        // Filter by questionIndices if provided
        let challengeSet = normalized;
        if (Array.isArray(challenge.questionIndices) && challenge.questionIndices.length > 0) {
          challengeSet = challenge.questionIndices
            .map((i) => normalized[i])
            .filter(Boolean);
        }

        if (challengeSet.length === 0) {
          challengeSet = normalized.slice(0, challenge.questionCount || 10);
        }

        if (isMounted) {
          if (challengeSet.length === 0) {
            setErrorMsg("No quiz questions available for this reviewer.");
          } else {
            setQuestions(challengeSet);
            startTimeRef.current = Date.now();
            setTimeLeft(timePerQ);
          }
        }
      } catch (err) {
        if (isMounted) {
          setErrorMsg(err.response?.data?.message || err.message || "Failed to load quiz questions.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchQuestions();

    return () => {
      isMounted = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, challenge, groupId, timePerQ]);

  // Focus input automatically whenever current question changes to text input
  useEffect(() => {
    setSelectedOption(null);
    setTextInput("");
    const q = questions[currentIndex];
    if (q && (!q.options || q.options.length === 0)) {
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 50);
    }
  }, [currentIndex, questions]);

  const handleAutoAdvance = () => {
    const currentQ = questions[currentIndex];
    const hasOptions = Array.isArray(currentQ?.options) && currentQ.options.length > 0;
    const currentAns = hasOptions ? selectedOption : textInput.trim();

    const updatedAnswers = {
      ...userAnswers,
      [currentIndex]: currentAns || "__TIMEOUT__",
    };
    setUserAnswers(updatedAnswers);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((idx) => idx + 1);
      setSelectedOption(null);
      setTextInput("");
    } else {
      finalizeSubmission(updatedAnswers);
    }
  };

  const handleAutoAdvanceRef = useRef(handleAutoAdvance);
  useEffect(() => {
    handleAutoAdvanceRef.current = handleAutoAdvance;
  });

  // Handle per-question timer
  useEffect(() => {
    if (!isOpen || loading || errorMsg || questions.length === 0) return;

    setTimeLeft(timePerQ);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleAutoAdvanceRef.current();
          return timePerQ;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentIndex, isOpen, loading, errorMsg, questions.length, timePerQ]);

  const handleSelectOption = (opt) => {
    setSelectedOption(opt);
  };

  const handleNext = () => {
    if (timerRef.current) clearInterval(timerRef.current);

    const currentQ = questions[currentIndex];
    if (!currentQ) return;
    const hasOptions = Array.isArray(currentQ.options) && currentQ.options.length > 0;
    const currentAns = hasOptions ? selectedOption : textInput.trim();

    const isCorrect = isAnswerCorrect(currentQ, currentAns);

    if (isCorrect) {
      setStreak((s) => s + 1);
    } else {
      setStreak(0);
    }

    const updatedAnswers = {
      ...userAnswers,
      [currentIndex]: currentAns || "__UNANSWERED__",
    };
    setUserAnswers(updatedAnswers);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
      setSelectedOption(null);
      setTextInput("");
    } else {
      finalizeSubmission(updatedAnswers);
    }
  };

  const finalizeSubmission = async (finalAnswersMap) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSubmitting(true);

    const answers = finalAnswersMap || userAnswers;
    const now = Date.now();
    const totalDurationSeconds = Math.max(
      1,
      Math.round((now - (startTimeRef.current || now)) / 1000)
    );

    let correctCount = 0;
    questions.forEach((q, idx) => {
      const given = answers[idx];
      if (isAnswerCorrect(q, given)) {
        correctCount += 1;
      }
    });

    try {
      const result = await submitChallengeAttempt(groupId, challenge._id, {
        score: correctCount,
        totalQuestions: questions.length,
        timeSpentSeconds: totalDurationSeconds,
      });

      if (onAttemptSubmitted) {
        onAttemptSubmitted(result);
      }
      onClose();
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message || err.message || "Failed to submit challenge results."
      );
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const currentQ = questions[currentIndex];
  const progressPct = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const timerPct = (timeLeft / timePerQ) * 100;
  const qType = currentQ ? inferQuizType(currentQ) : "mcq";
  const hasOptions = Array.isArray(currentQ?.options) && currentQ.options.length > 0;

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/80 p-3 sm:p-5 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col border border-indigo-100 animate-in zoom-in-95 duration-200 min-h-[500px] max-h-[92vh]">
        {/* Header Bar */}
        <div className="px-6 py-4 bg-gradient-to-r from-violet-900 via-indigo-900 to-purple-950 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-xl bg-amber-400 text-amber-950 flex items-center justify-center text-sm font-black shadow-sm">
              ⚡
            </span>
            <div>
              <h2 className="text-sm font-extrabold tracking-tight truncate max-w-xs sm:max-w-md">
                {challenge?.title || "Challenge Arena"}
              </h2>
              <span className="text-[11px] text-indigo-300">
                Question {currentIndex + 1} of {questions.length}
              </span>
            </div>
          </div>

          {/* Streak indicator */}
          <div className="flex items-center gap-3">
            {streak > 1 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black bg-amber-400 text-amber-950 animate-bounce shadow-sm">
                🔥 {streak} Streak
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white transition flex items-center justify-center"
              aria-label="Exit Challenge"
              title="Exit Arena"
            >
              <i className="fa-solid fa-xmark text-sm"></i>
            </button>
          </div>
        </div>

        {/* Global Progress Line */}
        <div className="w-full bg-gray-100 h-1.5 shrink-0">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Question Area */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 flex flex-col justify-center">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <i className="fa-solid fa-circle-notch fa-spin text-3xl mb-3 text-indigo-600"></i>
              <p className="text-sm font-semibold text-gray-600">Loading challenge questions...</p>
            </div>
          ) : errorMsg ? (
            <div className="p-6 rounded-2xl bg-rose-50 border border-rose-200 text-center space-y-3">
              <i className="fa-solid fa-triangle-exclamation text-rose-500 text-3xl"></i>
              <p className="text-sm font-bold text-rose-800">{errorMsg}</p>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-rose-600 text-white hover:bg-rose-700 transition"
              >
                Close
              </button>
            </div>
          ) : currentQ ? (
            <div className="space-y-6 my-auto">
              {/* Question Ticker, Type Badge & Timer Indicator */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-extrabold text-indigo-600 uppercase tracking-wider">
                    Question {currentIndex + 1}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                      qType === "fill-blanks"
                        ? "bg-amber-100 text-amber-800 border border-amber-200"
                        : qType === "identification"
                        ? "bg-purple-100 text-purple-800 border border-purple-200"
                        : qType === "true-false"
                        ? "bg-blue-100 text-blue-800 border border-blue-200"
                        : "bg-indigo-100 text-indigo-800 border border-indigo-200"
                    }`}
                  >
                    {qType === "fill-blanks"
                      ? "Fill in the Blank"
                      : qType === "identification"
                      ? "Identification"
                      : qType === "true-false"
                      ? "True or False"
                      : "Multiple Choice"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-28 sm:w-36 bg-gray-100 rounded-full h-2 overflow-hidden border border-gray-200">
                    <div
                      className={`h-full transition-all duration-1000 ${
                        timerPct <= 25
                          ? "bg-rose-500"
                          : timerPct <= 50
                          ? "bg-amber-500"
                          : "bg-emerald-500"
                      }`}
                      style={{ width: `${timerPct}%` }}
                    />
                  </div>
                  <span
                    className={`font-mono text-xs font-black px-2 py-0.5 rounded-md ${
                      timeLeft <= 5
                        ? "bg-rose-100 text-rose-700 animate-ping"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {timeLeft}s
                  </span>
                </div>
              </div>

              {/* Prompt */}
              <div className="p-5 rounded-2xl bg-indigo-50/50 border border-indigo-100/80 shadow-xs">
                <p className="text-base sm:text-lg font-bold text-gray-900 leading-snug">
                  {currentQ.prompt}
                </p>
              </div>

              {/* Multiple Choice Options OR Text Input for Identification & Fill-in-the-Blank */}
              {hasOptions ? (
                <div className="grid grid-cols-1 gap-2.5">
                  {currentQ.options.map((opt, i) => {
                    const isSelected = selectedOption === opt;
                    const letter = String.fromCharCode(65 + i);

                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleSelectOption(opt)}
                        className={`w-full text-left p-4 rounded-2xl border text-sm font-semibold transition-all duration-150 flex items-center gap-3.5 ${
                          isSelected
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-md scale-[1.01]"
                            : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                        }`}
                      >
                        <span
                          className={`w-7 h-7 rounded-xl flex items-center justify-center text-xs font-black shrink-0 ${
                            isSelected ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {letter}
                        </span>
                        <span className="flex-1">{opt}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <label
                      htmlFor="challenge-text-answer"
                      className="font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <i className="fa-solid fa-pen-nib text-indigo-600"></i>
                      <span>
                        {qType === "fill-blanks"
                          ? "Type the missing word or phrase:"
                          : "Type your identification answer:"}
                      </span>
                    </label>
                    <span className="text-gray-400 font-medium hidden sm:inline">
                      Press <kbd className="px-1.5 py-0.5 rounded bg-gray-100 border text-[10px] font-mono text-gray-600 font-bold">Enter ↵</kbd> to advance
                    </span>
                  </div>

                  <div className="relative">
                    <input
                      ref={textInputRef}
                      id="challenge-text-answer"
                      type="text"
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleNext();
                        }
                      }}
                      autoComplete="off"
                      spellCheck="false"
                      placeholder={
                        qType === "fill-blanks"
                          ? "Type the missing word or phrase..."
                          : "Type the term being identified..."
                      }
                      className="w-full px-5 py-4 rounded-2xl border-2 border-indigo-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 bg-white text-base sm:text-lg font-semibold text-gray-900 placeholder:text-gray-400 shadow-sm outline-none transition-all"
                    />
                    {textInput && (
                      <button
                        type="button"
                        onClick={() => {
                          setTextInput("");
                          textInputRef.current?.focus();
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex items-center justify-center text-xs transition"
                        title="Clear input"
                      >
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    )}
                  </div>

                  <p className="text-[11px] text-gray-400 italic">
                    {qType === "fill-blanks"
                      ? "Tip: Match the missing word accurately. Case does not matter."
                      : "Tip: Provide the specific term or noun defined in the question."}
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer Next Button */}
        {!loading && !errorMsg && questions.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between shrink-0">
            <span className="text-xs text-gray-400">
              {currentIndex + 1} of {questions.length} questions
            </span>

            <button
              type="button"
              onClick={handleNext}
              disabled={submitting}
              className="px-6 py-2.5 rounded-xl text-xs font-black bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-600/30 transition active:scale-95 flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin"></i>
                  <span>Calculating Points...</span>
                </>
              ) : currentIndex === questions.length - 1 ? (
                <>
                  <span>Submit Challenge 🚀</span>
                </>
              ) : (
                <>
                  <span>Next Question</span>
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChallengeQuizModal;
